import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OnboardingHeader from '../../components/hospitalOnboarding/OnboardingHeader';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import {
  Mic,
  FileText,
  Edit3,
  ArrowRight,
  ShieldCheck,
  Clock,
  Lock,
  Building2,
  Truck,
  Volume2
} from 'lucide-react';

export const HospitalOnboardingEntryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formData, setCurrentStep, applicationId, showToast } = useHospitalOnboarding();

  const urlType = searchParams.get('type');
  const partnerType = urlType || formData.partnerType || localStorage.getItem('risa_partner_type') || 'hospital';
  const isFleet = partnerType === 'fleet';

  const urlAppId = searchParams.get('applicationId');
  const storedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem('risa_current_application_id') : null;
  const rawAppId =
    urlAppId ||
    applicationId ||
    storedAppId ||
    formData.application_id;

  const isValidAppId =
    typeof rawAppId === 'string' &&
    rawAppId.trim().length > 0 &&
    !rawAppId.startsWith('RISA-APP-');

  const activeAppId = isValidAppId ? rawAppId : null;
  const isRegistered = !!activeAppId;

  // Application guard: Redirect to registration if no application ID exists
  React.useEffect(() => {
    if (!isRegistered) {
      if (showToast) {
        showToast('Please complete initial partner registration first.', 'error');
      }
      navigate(`/hospital/register?type=${partnerType}`, { replace: true });
    }
  }, [isRegistered, partnerType, navigate, showToast]);

  const handleStartManual = () => {
    const maxSteps = isFleet ? 6 : 5;
    const targetStep =
      formData.current_step && formData.current_step >= 1 && formData.current_step <= maxSteps
        ? formData.current_step
        : 1;
    setCurrentStep(targetStep);
    navigate(`/hospital/onboarding/manual?applicationId=${activeAppId || ''}&type=${partnerType}`);
  };

  const handleStartVoice = () => {
    navigate(`/hospital/onboarding/voice?applicationId=${activeAppId || ''}&type=${partnerType}`);
  };

  if (!isRegistered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-slate-200">
          <p className="text-sm font-medium text-slate-700">Please complete initial partner registration first. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col font-sans relative overflow-x-hidden"
      style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
      }}
    >
      {/* Decorative ambient subtle background glow & dots */}
      <div className="absolute top-16 left-6 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute -top-10 right-10 w-80 h-80 bg-blue-50/50 rounded-full blur-2xl pointer-events-none -z-10" />

      {/* Top Header */}
      <OnboardingHeader />

      {/* Main Choice Screen */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14 flex flex-col items-center justify-center">
        {/* Dynamic Onboarding Pill (Hospital vs Fleet) */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200/70 mb-3 select-none shadow-2xs">
          {isFleet ? (
            <>
              <Truck className="w-3.5 h-3.5 text-blue-600" />
              <span>Ambulance fleet onboarding</span>
            </>
          ) : (
            <>
              <Building2 className="w-3.5 h-3.5 text-blue-600" />
              <span>Hospital onboarding</span>
            </>
          )}
        </div>

        {/* Hero Title */}
        <h1 className="text-2xl sm:text-3xl lg:text-[34px] font-semibold text-slate-800 tracking-tight text-center mb-2.5">
          How would you like to get started?
        </h1>

        {/* Subtitle */}
        <p className="text-xs sm:text-sm text-slate-500 max-w-lg text-center mb-10 sm:mb-12 leading-relaxed font-normal">
          {isFleet
            ? 'Complete your fleet onboarding by voice or step-by-step form. You can switch at any time without losing your answers.'
            : 'Use your voice or fill in the form. You can switch at any time without losing your answers.'}
        </p>

        {/* Main Section: 3 Left Feature Badges + 2 Choice Cards */}
        <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center lg:items-center justify-center gap-8 lg:gap-12">
          {/* Left Side: 3 Feature Badges */}
          <div className="flex flex-col sm:flex-row lg:flex-col gap-5 sm:gap-7 lg:gap-6 w-full lg:w-64 shrink-0 justify-center">
            {/* Feature 1 */}
            <div className="flex items-start gap-3.5 text-left">
              <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200/70 flex items-center justify-center text-blue-600 shrink-0 shadow-2xs">
                <ShieldCheck className="w-4.5 h-4.5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-700 leading-tight">Quick & easy</h3>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug font-normal">Simple step-by-step process</p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex items-start gap-3.5 text-left">
              <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200/70 flex items-center justify-center text-blue-600 shrink-0 shadow-2xs">
                <Clock className="w-4.5 h-4.5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-700 leading-tight">Flexible</h3>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug font-normal">Switch between voice and manual anytime</p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="flex items-start gap-3.5 text-left">
              <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200/70 flex items-center justify-center text-blue-600 shrink-0 shadow-2xs">
                <Lock className="w-4.5 h-4.5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-700 leading-tight">Your data is safe</h3>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug font-normal">Secure and confidential</p>
              </div>
            </div>
          </div>

          {/* Right Side: 2 Prominent Choice Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 w-full max-w-2xl">
            {/* Card 1: By voice */}
            <div
              onClick={handleStartVoice}
              className="bg-white rounded-3xl border border-slate-200/80 p-7 sm:p-8 flex flex-col items-center text-center shadow-xs hover:shadow-xl hover:border-blue-400 transition-all duration-200 cursor-pointer group"
            >
              {/* Circular Avatar with Mic & Speech waves */}
              <div className="w-20 h-20 rounded-full bg-blue-50/80 border border-blue-100 flex items-center justify-center relative mb-5 group-hover:scale-105 transition-transform duration-200">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <Mic className="w-6 h-6 text-blue-600" />
                </div>
                <div className="absolute top-1 right-0 bg-white border border-blue-200 rounded-full px-1.5 py-0.5 shadow-2xs flex items-center gap-1 text-blue-600">
                  <Volume2 className="w-2.5 h-2.5 text-blue-600" />
                </div>
              </div>

              <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-1.5">By voice</h2>
              <p className="text-xs text-slate-500 mb-6 max-w-[220px] flex-1 leading-relaxed font-normal">
                We'll ask one question at a time. Speak, review and confirm.
              </p>

              <button
                type="button"
                className="w-full py-2.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs sm:text-sm shadow-xs flex items-center justify-center gap-2 group-hover:gap-3 transition-all cursor-pointer"
              >
                <span>Start voice onboarding</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Card 2: By manual entry */}
            <div
              onClick={handleStartManual}
              className="bg-white rounded-3xl border border-slate-200/80 p-7 sm:p-8 flex flex-col items-center text-center shadow-xs hover:shadow-xl hover:border-blue-400 transition-all duration-200 cursor-pointer group"
            >
              {/* Circular Avatar with Document & Pencil (Smart Ambulance Blue Theme) */}
              <div className="w-20 h-20 rounded-full bg-blue-50/80 border border-blue-100 flex items-center justify-center relative mb-5 group-hover:scale-105 transition-transform duration-200">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div className="absolute top-1 right-0 bg-blue-600 text-white rounded-full p-1.5 shadow-2xs flex items-center justify-center">
                  <Edit3 className="w-3 h-3 text-white" />
                </div>
              </div>

              <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-1.5">By manual entry</h2>
              <p className="text-xs text-slate-500 mb-6 max-w-[220px] flex-1 leading-relaxed font-normal">
                Fill in the familiar forms at your own pace, step by step.
              </p>

              <button
                type="button"
                className="w-full py-2.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs sm:text-sm shadow-xs flex items-center justify-center gap-2 group-hover:gap-3 transition-all cursor-pointer"
              >
                <span>Start manual entry</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Existing draft notice */}
        {formData.status === 'IN_PROGRESS' && (
          <p className="mt-8 text-xs text-slate-500 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-2xs">
            You have an in-progress draft saved at Step {formData.current_step || 1}.
          </p>
        )}
      </main>

      {/* Minimal Footer */}
      <footer className="py-5 text-center text-xs text-slate-400 border-t border-slate-200/80 bg-white">
        © {new Date().getFullYear()} RISA Smart Ambulance Platform. All rights reserved.
      </footer>
    </div>
  );
};

export default HospitalOnboardingEntryPage;
